/**
 *
 * AppInitPage
 *
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { Container, Row } from 'reactstrap';
import AriadneLoading from 'components/ariadne/AriadneLoading/AriadneLoading';
import history from 'utils/history';
import {
  initSagaLibrarySelector,
  userDetailsSelector,
  errorSelector,
  configDetailsSelector,
  configErrorSelector,
  consentBypassStatusSelector,
  lhAccountConsentDataSelector,
} from 'domains/session/selectors';
import {
  getUserDetails,
  putIsAppReadyAction,
  getConfigDetailsAction,
  postLoginCountAction,
  setConsentBypassStatusAction,
  getLHAccountConsentAction,
} from 'domains/session/actions';
import { LAWHUB_ACCOUNT_CREATION_PURPOSE_CODE } from 'domains/session/constants';
import { useSessionReduxAndSaga } from 'domains/session/hooks';
import { getUser } from '../../authProvider';
import { getAndPersistSearchParamForSession } from '../../helpers/historyHelper';
import { initializePersistence } from '../../helpers/persistence/persistenceManagementHelper';
import { config } from '../../config';
import {
  saveDataFromLocalStorage,
  setupTimedSave,
} from '../../helpers/telemetryHelper';
import {
  STARTUP_PATH,
  WRITING_EXAM_PAGE,
  WRITING_ERROR_SCREEN_PATH,
  SCORE_REPORT_PATH,
  PROCTORING_INITIALIZE_PATH,
  EXAM_START_PATH,
} from '../App/constants';
import { EXAM_PATH_PREFIX } from '../../helpers/constants';

// we have a pretty complex flow, so let's track current state in a state machine
const pageStates = {
  STATE_WAITING_FOR_SAGA: 0,
  STATE_READY_TO_REQUEST_USER_DETAILS: 1,
  STATE_READY_TO_REQUEST_CONFIG_DETAILS: 12,
  STATE_WAITING_FOR_USER_DETAILS: 2,
  STATE_RETRIEVED_USER_DETAILS: 3,
  STATE_USER_DETAILS_ERROR: 10,
  STATE_WAITING_FOR_CONFIG_DETAILS: 4,
  STATE_RETRIEVED_CONFIG_DETAILS: 5,
  STATE_CONFIG_ERROR: 11,
  STATE_INITIALIZING_PERSISTENCE: 6,
  STATE_APP_READY: 7,
  STATE_CHECKING_CONSENT: 8,
  STATE_CONSENT_CHECKED: 9,
};

let configurationChangesIntervalId = null;
let configurationChangesInterval = parseInt(
  config.REACT_APP_CONFIG_POLLING_INTERVAL,
  10,
);
// Referral code key in search params
const REFERRAL_CODE_KEY = 'refcode';

export function AppInitPage({
  initSagaLibrary,
  userDetails,
  onLoadUserDetails,
  onPutIsAppReady,
  onPostLoginCount,
  onGetConfigDetails,
  onSetConsentBypassStatus,
  onGetLHAccountConsent,
  configDetails,
  configError,
  userDetailsError,
  consentBypassStatus,
  lhAccountConsentData,
}) {
  useSessionReduxAndSaga();
  const [state, setState] = useState(pageStates.STATE_WAITING_FOR_SAGA);

  useEffect(() => {
    switch (state) {
      case pageStates.STATE_WAITING_FOR_SAGA:
        // Persist referral code search param in sessionStorage
        getAndPersistSearchParamForSession(REFERRAL_CODE_KEY);

        if (initSagaLibrary) {
          setState(pageStates.STATE_READY_TO_REQUEST_CONFIG_DETAILS);
        }
        break;
      case pageStates.STATE_READY_TO_REQUEST_CONFIG_DETAILS:
        setState(pageStates.STATE_WAITING_FOR_CONFIG_DETAILS);
        onGetConfigDetails();
        break;
      case pageStates.STATE_WAITING_FOR_CONFIG_DETAILS:
        if (configDetails) {
          setState(pageStates.STATE_READY_TO_REQUEST_USER_DETAILS);
        } else if (configError) {
          onGetConfigDetails();
        }
        break;
      case pageStates.STATE_READY_TO_REQUEST_USER_DETAILS:
        if (getUser()) {
          setState(pageStates.STATE_WAITING_FOR_USER_DETAILS);

          if (config.REACT_APP_IS_CONSENT_MODAL_ENABLED === 'true') {
            // Get the original intended URL from query params
            const query = new URLSearchParams(window.location.search);
            const originalUrl = query.get('url');

            // Check if current route should bypass consent (for both new and existing users)
            const routeBypass = shouldBypassConsent(originalUrl);

            // For existing users, also check if they've dismissed consent modal
            const consentModalDismissed =
              sessionStorage.getItem('consentModalDismissed') === 'true';

            const bypassConsent = routeBypass || consentModalDismissed;

            // Store the bypass status in Redux state BEFORE loading user details
            onSetConsentBypassStatus(bypassConsent);
          }

          onLoadUserDetails();
          break;
        }
        setState(pageStates.STATE_RETRIEVED_CONFIG_DETAILS);
        break;
      case pageStates.STATE_WAITING_FOR_USER_DETAILS:
        if (userDetails) {
          onPostLoginCount();

          // Check consentPurposes from userDetails first, if present
          const consentPurposes = userDetails.consentPurposes || [];
          const lawhubConsentPurpose = consentPurposes.find(
            purpose =>
              purpose?.purposeCode === LAWHUB_ACCOUNT_CREATION_PURPOSE_CODE,
          );

          if (config.REACT_APP_IS_CONSENT_MODAL_ENABLED === 'true') {
            if (lawhubConsentPurpose) {
              // Backend already told us whether LawHub account creation consent is granted
              if (lawhubConsentPurpose.isGranted) {
                // Treat consent as checked and valid
                setState(pageStates.STATE_CONSENT_CHECKED);
              } else {
                // Consent not granted - reload app to trigger consent flow
                reloadAppForConsentFlow();
              }
            } else if (!consentBypassStatus) {
              // No consentPurposes info for LawHub; fall back to consent API
              setState(pageStates.STATE_CHECKING_CONSENT);
              onGetLHAccountConsent();
            } else {
              // Consent is bypassed; proceed as if consent is satisfied
              setState(pageStates.STATE_RETRIEVED_USER_DETAILS);
            }
          } else {
            // Consent modal disabled; proceed as before
            setState(pageStates.STATE_RETRIEVED_USER_DETAILS);
          }
        }
        if (userDetailsError) {
          // Don't retry if it's a 404 error when consent modal is enabled
          // (user was redirected for consent)
          if (
            userDetailsError.response?.status === 404 &&
            config.REACT_APP_IS_CONSENT_MODAL_ENABLED === 'true'
          ) {
            // Stay in waiting state, user has been redirected for consent
          } else {
            onLoadUserDetails();
          }
        }
        break;
      case pageStates.STATE_CHECKING_CONSENT:
        if (lhAccountConsentData) {
          // Check if all subpurposes have 'CONFIRMED' choice from consent API
          const hasValidConsentFromLhAccount = lhAccountConsentData?.consent?.purposes?.every(
            purpose =>
              purpose?.subPurposes?.every(
                subpurpose => subpurpose.choice === 'CONFIRMED',
              ),
          );

          // Also consider consentPurposes on userDetails, if present
          const consentPurposes = userDetails?.consentPurposes || [];
          const lawhubConsentPurpose = consentPurposes.find(
            purpose =>
              purpose?.purposeCode === LAWHUB_ACCOUNT_CREATION_PURPOSE_CODE,
          );
          const hasValidConsentFromUserDetails = Boolean(
            lawhubConsentPurpose && lawhubConsentPurpose.isGranted,
          );

          const hasValidConsent =
            hasValidConsentFromUserDetails || hasValidConsentFromLhAccount;

          if (hasValidConsent) {
            setState(pageStates.STATE_CONSENT_CHECKED);
          } else {
            // Reload app to trigger consent flow when consent is not confirmed
            reloadAppForConsentFlow();
          }
        }
        break;
      case pageStates.STATE_RETRIEVED_CONFIG_DETAILS:
      case pageStates.STATE_RETRIEVED_USER_DETAILS:
      case pageStates.STATE_CONSENT_CHECKED:
        setState(pageStates.STATE_INITIALIZING_PERSISTENCE);
        if (getUser()) {
          initializePersistence().then(() => {
            setupTimedSave();
            setState(pageStates.STATE_APP_READY);
            onPutIsAppReady();

            // Save Outstanding Telemetry data from local storage when a user logs in
            saveDataFromLocalStorage();
          });
        } else {
          setState(pageStates.STATE_APP_READY);
          onPutIsAppReady();
        }
        break;
      case pageStates.STATE_APP_READY:
        {
          startConfigurationChangesInterval();
          startConfigurationUpdateHelperInterval();

          const query = new URLSearchParams(window.location.search);
          const orig = query.get('url');
          history.replace(orig);
        }
        break;
      default:
        break;
    }
  }, [
    state,
    initSagaLibrary,
    userDetails,
    configDetails,
    userDetailsError,
    configError,
    consentBypassStatus,
    lhAccountConsentData,
  ]);

  function startConfigurationChangesInterval() {
    configurationChangesIntervalId = window.setInterval(() => {
      onGetConfigDetails();
      if (getUser()) {
        // Don't call getUserDetails if we're in a consent redirect scenario
        if (
          !(
            config.REACT_APP_IS_CONSENT_MODAL_ENABLED === 'true' &&
            window.location.search?.includes('tonext')
          )
        ) {
          onLoadUserDetails();
        }
      }
    }, parseInt(config.REACT_APP_CONFIG_POLLING_INTERVAL, 10) * 1000);
  }

  function startConfigurationUpdateHelperInterval() {
    window.setInterval(() => {
      if (
        configurationChangesInterval !==
        parseInt(config.REACT_APP_CONFIG_POLLING_INTERVAL, 10)
      ) {
        configurationChangesInterval = parseInt(
          config.REACT_APP_CONFIG_POLLING_INTERVAL,
          10,
        );

        stopConfigurationChangesInterval();
        startConfigurationChangesInterval();
      }
    }, 1000);
  }

  function stopConfigurationChangesInterval() {
    clearInterval(configurationChangesIntervalId);
  }

  /**
   * Reload the application to trigger Next.js reverse proxy consent flow
   */
  function reloadAppForConsentFlow() {
    if (!window.location.search?.includes('tonext')) {
      window.location = `${window.location.origin}?tonext=true`;
    }
  }

  /**
   * Check if the current route should bypass consent based on the original intended URL
   * @param {string} originalUrl - The original intended URL from query params
   * @returns {boolean} - True if consent should be bypassed, false otherwise
   */
  function shouldBypassConsent(originalUrl) {
    if (!originalUrl) {
      return false;
    }

    const CONSENT_BYPASS_ROUTES = [
      // Configuration route
      STARTUP_PATH,
      // LSAT test-taking workflows (bypass LawHub consent)
      EXAM_PATH_PREFIX,
      EXAM_START_PATH,
      PROCTORING_INITIALIZE_PATH,
      WRITING_EXAM_PAGE,
      WRITING_ERROR_SCREEN_PATH,
      SCORE_REPORT_PATH,
    ];

    return CONSENT_BYPASS_ROUTES.some(route => originalUrl.startsWith(route));
  }

  return (
    <Container fluid style={{ width: '100vw' }}>
      <Row>
        <AriadneLoading text="session data" />
      </Row>
    </Container>
  );
}

AppInitPage.propTypes = {
  initSagaLibrary: PropTypes.bool.isRequired,
  userDetails: PropTypes.object,
  onLoadUserDetails: PropTypes.func,
  onPutIsAppReady: PropTypes.func,
  onPostLoginCount: PropTypes.func,
  onGetConfigDetails: PropTypes.func,
  onSetConsentBypassStatus: PropTypes.func,
  onGetLHAccountConsent: PropTypes.func,
  configDetails: PropTypes.object,
  configError: PropTypes.object,
  userDetailsError: PropTypes.object,
  consentBypassStatus: PropTypes.bool,
  lhAccountConsentData: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  initSagaLibrary: initSagaLibrarySelector(),
  userDetails: userDetailsSelector(),
  configDetails: configDetailsSelector(),
  configError: configErrorSelector(),
  userDetailsError: errorSelector(),
  consentBypassStatus: consentBypassStatusSelector(),
  lhAccountConsentData: lhAccountConsentDataSelector(),
});

export function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    onLoadUserDetails: () => dispatch(getUserDetails()),
    onPostLoginCount: () => dispatch(postLoginCountAction()),
    onPutIsAppReady: () => dispatch(putIsAppReadyAction()),
    onGetConfigDetails: () => dispatch(getConfigDetailsAction()),
    onSetConsentBypassStatus: bypassConsent =>
      dispatch(setConsentBypassStatusAction(bypassConsent)),
    onGetLHAccountConsent: () => dispatch(getLHAccountConsentAction()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(withConnect)(AppInitPage);
